<script setup lang="ts">
import Button from "@/components/ui/button/Button.vue";
import Card from "@/components/ui/card/Card.vue";
import CardContent from "@/components/ui/card/CardContent.vue";
import CardHeader from "@/components/ui/card/CardHeader.vue";
import Item from "@/components/ui/item/Item.vue";
import ItemActions from "@/components/ui/item/ItemActions.vue";
import ItemTitle from "@/components/ui/item/ItemTitle.vue";
import { useEventsStore } from "@/stores/events.js";
import HeaderView from "@/views/HeaderView.vue";
import SignUpView from "@/views/SignUpView.vue";
import { CheckIcon } from "lucide-vue-next";
import { computed, ref } from "vue";
import { useUserDataStore } from "../stores/userData";
import { useRoute, useRouter } from "vue-router";
import Dialog from "@/components/ui/dialog/Dialog.vue";
import DialogTrigger from "@/components/ui/dialog/DialogTrigger.vue";
import DialogContent from "@/components/ui/dialog/DialogContent.vue";
import DialogHeader from "@/components/ui/dialog/DialogHeader.vue";
import DialogTitle from "@/components/ui/dialog/DialogTitle.vue";
import DialogDescription from "@/components/ui/dialog/DialogDescription.vue";
import DialogFooter from "@/components/ui/dialog/DialogFooter.vue";
import DialogClose from "@/components/ui/dialog/DialogClose.vue";
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import Navigation from "@/components/Navigation.vue";

const route = useRoute();
const router = useRouter();


// const bookedSeat = ref([null, null]);

function handleSeatChange(value) {
  bookedSeat.value = [value.rowIndex, value.colIndex];
}
function handleClickedOnSelected() {
  bookedSeat.value = [null, null];
}

function transformBookedSeatToLetter(value) {
  if (value <= 1 && value >= 0) {
    return String.fromCharCode(65 + value);
  }
  return String.fromCharCode(64 + value);
}

function confirmBooking() {
  if(bookedSeat.value[0] == null){
    createBooking();
  }
  if(bookedSeat.value[0] != null && event?.value.spots[bookedSeat.value[0]].row[bookedSeat.value[1]] == userId) {
    createBooking();
  }
  if(bookedSeat.value[0] != null && !event?.value.spots[bookedSeat.value[0]].row[bookedSeat.value[1]]){
    eventsStore.updateSeatBooking(
      event?.value.id,
      bookedSeat.value,
      userId,
      originalSeat.value
    );
    originalSeat.value = bookedSeat.value;
    createBooking();
  } else {
    bookingError();
  }
}

function bookingError(){
  isErrorOpen.value = true;
}
function errorOk(){
  isErrorOpen.value = false;
  bookedSeat.value = originalSeat.value;
}

function createBooking() {
  // update user signed up
  userDataStore.addEvent(event?.value.id);

  // route to confirmation page
  router.replace({ name: "Confirmation", params: { id: event?.value.id } });
}

const storedDarkMode =
  localStorage.getItem("darkMode") === "false" ? false : true;

const isDarkMode = ref(storedDarkMode);

function removeSeatBooking() {
  eventsStore.updateSeatBooking(
    event?.value.id,
    [null, null],
    userId,
    originalSeat.value
  );
  bookedSeat.value = [null, null];
  originalSeat.value = [null, null];
}

const currentEventId = route.params.id;
// Display error if eventId is undefined or event not found
if (!currentEventId) {
  // TODO: Replace with proper user notification
  console.error("Event ID is undefined in route parameters.");
  router.replace({ path: "/" });
}

const eventsStore = useEventsStore();
var event = computed(() => eventsStore.getEventById(currentEventId));

if (!event.value) {
  // TODO: Replace with proper user notification
  console.error("Event not found for the given ID.");
  router.replace({ path: "/" });
}

const userDataStore = useUserDataStore();
const userId = userDataStore.steamId;

// find if user has already booked a seat for this event
const existingBooking = computed(() => {
  if (!event.value) return [null, null];
  const booking = eventsStore.getSeatBookingForUser(event?.value, userId);
  return booking ? booking : [null, null];
});

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("lg"); // true if screen width is less than 'lg' breakpoint

const bookedSeat = ref(existingBooking.value);
const originalSeat = ref(existingBooking.value);
const username = computed(() => userDataStore.userProfile?.nickname);
const avatarUrl = computed(() => userDataStore.avatarUrl);
const isLoggedIn = computed(() => userDataStore.isLoggedIn);
const isErrorOpen = ref(false);
</script>

<template>
  <div v-if="!isMobile" class="w-full h-20">
    <Navigation
      :username="username"
      :avatarUrl="avatarUrl"
      :isLoggedIn="isLoggedIn"
    ></Navigation>
  </div>
  <Card
    v-if="!isMobile"
    class="w-full h-full rounded-0 border-0 shadow-none bg-background lg:pt-0 lg:gap-0 px-4 lg:px-22"
  >
    <div
      class="inset-0 z-10 w-full h-50 mb-4"
      style="
        background-image: url('https://images.unsplash.com/photo-1636487658609-28282bb5a3a0?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Z2FtaW5nJTIwY2hhaXJ8ZW58MHx8MHx8fDA%3D');
        background-size: cover;
        background-position: center;
      "
    >
      <div
        class="flex items-center justify-center h-full text-3xl font-semibold bg-black/50 text-white"
      >
        Book seat for "{{ event?.name }}"
      </div>
    </div>
  </Card>

  <div class="bg-background min-h-screen pb-28">
    <Card
      class="rounded-none border-none z-50 shadow-none bg-background lg:py-0 gap-0"
    >
      <CardHeader>
        <HeaderView
          v-if="isMobile"
          :name="event?.name"
          :icon="true"
          @removeSeat="removeSeatBooking"
        ></HeaderView>
      </CardHeader>
      <CardContent class="flex justify-center">
        <SignUpView
          :seats="event?.spots"
          :bookedSeat="bookedSeat"
          @onSeatChange="handleSeatChange"
          @onClickedOnSelected="handleClickedOnSelected"
          @onClickConfirm="confirmBooking"
        >
        </SignUpView>
      </CardContent>
    </Card>

    <div class="fixed bottom-4 right-0">
      <Item
        v-if="
          !(
            bookedSeat[0] !== null &&
            bookedSeat[1] !== null &&
            (bookedSeat[0] !== originalSeat[0] ||
              bookedSeat[1] !== originalSeat[1])
          )
        "
        variant="outline"
        class="sticky bottom-4 z-100 bg-secondary dark:bg-secondary m-4"
      >
        <ItemTitle class="text-xl text-gray-700 dark:text-primary-foreground"
          >Skip Booking Seat</ItemTitle
        >
        <ItemActions class="ml-auto">
          <Dialog>
            <DialogTrigger as-child>
              <Button>Confirm <CheckIcon /></Button>
            </DialogTrigger>
            <DialogContent
              class="sm:max-w-[425px]"
              :class="{ dark: isDarkMode }"
            >
              <DialogHeader
                class="dark:text-white"
                :class="{ dark: isDarkMode }"
              >
                <DialogTitle>Continue without seat</DialogTitle>
                <DialogDescription>
                  Are you sure you want to continue without a seat?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose as-child>
                  <Button variant="outline"> Cancel </Button>
                </DialogClose>
                <DialogClose as-child>
                  <Button type="submit" @click="confirmBooking"> Book </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </ItemActions>
      </Item>
      <Item
        v-if="
          bookedSeat[0] !== null &&
          bookedSeat[1] !== null &&
          (bookedSeat[0] !== originalSeat[0] ||
            bookedSeat[1] !== originalSeat[1])
        "
        variant="outline"
        class="sticky bottom-4 z-100 bg-secondary dark:bg-secondary m-4"
      >
        <ItemTitle class="text-xl text-gray-700 dark:text-primary-foreground"
          >Selected Seat: {{ transformBookedSeatToLetter(bookedSeat[1])
          }}{{ bookedSeat[0] }}</ItemTitle
        >
        <ItemActions class="ml-auto">
          <Dialog>
            <DialogTrigger as-child>
              <Button>Confirm <CheckIcon /></Button>
            </DialogTrigger>
            <DialogContent
              class="sm:max-w-[425px]"
              :class="{ dark: isDarkMode }"
            >
              <DialogHeader>
                <DialogTitle>Book Seat</DialogTitle>
                <DialogDescription>
                  Are you sure you want to book this seat?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose as-child>
                  <Button variant="outline"> Cancel </Button>
                </DialogClose>
                <DialogClose as-child>
                  <Button type="submit" @click="confirmBooking"> Book </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </ItemActions>
      </Item>
    </div>
    <Dialog :class="{ dark: isDarkMode }" v-model:open="isErrorOpen">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Error occurred while booking</DialogTitle>
          <DialogDescription>
            It seems this seat is already booked, please select a new seat.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="submit" @click="errorOk"> Ok </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style>
.dark {
  color: #fafafa;
}
</style>
